import type { LocalProject, ProjectSummary } from "../types";

const DB_NAME = "paper-bridge";
const DB_VERSION = 2;
const PROJECT_STORE = "projects";
const MIGRATION_NOTICE_KEY = "paper-bridge-layout-v2-migrated";

let databasePromise: Promise<IDBDatabase> | null = null;

function openDatabase(): Promise<IDBDatabase> {
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const database = request.result;
      if (!database.objectStoreNames.contains(PROJECT_STORE)) {
        const store = database.createObjectStore(PROJECT_STORE, {
          keyPath: "id"
        });
        store.createIndex("updatedAt", "updatedAt");
      } else if (event.oldVersion > 0 && event.oldVersion < 2) {
        request.transaction?.objectStore(PROJECT_STORE).clear();
        try {
          localStorage.setItem(MIGRATION_NOTICE_KEY, "1");
        } catch {
          // IndexedDB migration still succeeds if storage access is restricted.
        }
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return databasePromise;
}

function runRequest<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return openDatabase().then(
    (database) =>
      new Promise<T>((resolve, reject) => {
        const transaction = database.transaction(PROJECT_STORE, mode);
        const request = operation(transaction.objectStore(PROJECT_STORE));

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        transaction.onerror = () => reject(transaction.error);
      })
  );
}

export async function saveProject(project: LocalProject): Promise<void> {
  await runRequest("readwrite", (store) => store.put(project));
}

export async function getProject(
  projectId: string
): Promise<LocalProject | undefined> {
  return runRequest("readonly", (store) => store.get(projectId));
}

export async function deleteProject(projectId: string): Promise<void> {
  await runRequest("readwrite", (store) => store.delete(projectId));
}

export async function listProjects(): Promise<ProjectSummary[]> {
  const projects = await runRequest<LocalProject[]>("readonly", (store) =>
    store.getAll()
  );

  return projects
    .map((project) => {
      const blocks = project.layout.flatMap((page) => page.blocks);
      return {
        id: project.id,
        name: project.name,
        fileSize: project.fileSize,
        pageCount: project.pageCount,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        translatedCount: Object.values(project.translations).filter(
          (item) => item.status === "done" || item.status === "edited"
        ).length,
        translatableCount: blocks.filter((block) => block.translatable).length
      };
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function dataUrlToBlob(dataUrl: string): Promise<Blob> {
  const response = await fetch(dataUrl);
  return response.blob();
}

export async function createProjectBackup(
  project: LocalProject
): Promise<Blob> {
  const serializable = {
    format: "paper-bridge-project",
    version: 2,
    exportedAt: Date.now(),
    project: {
      ...project,
      pdf: await blobToDataUrl(project.pdf)
    }
  };
  const raw = new Blob([JSON.stringify(serializable)], {
    type: "application/json"
  });

  if ("CompressionStream" in window) {
    const stream = raw.stream().pipeThrough(new CompressionStream("gzip"));
    return new Response(stream).blob();
  }
  return raw;
}

export async function restoreProjectBackup(file: File): Promise<LocalProject> {
  let text: string;
  if (file.name.endsWith(".gz") && "DecompressionStream" in window) {
    const stream = file.stream().pipeThrough(new DecompressionStream("gzip"));
    text = await new Response(stream).text();
  } else {
    text = await file.text();
  }

  const parsed = JSON.parse(text) as {
    format?: string;
    version?: number;
    project?: Omit<LocalProject, "pdf"> & { pdf: string };
  };

  if (
    parsed.format !== "paper-bridge-project" ||
    !parsed.project ||
    typeof parsed.project.pdf !== "string"
  ) {
    throw new Error("这不是有效的 Paper Bridge 项目备份。");
  }
  if (parsed.version !== 2 || parsed.project.layoutVersion !== 2) {
    throw new Error(
      "这是旧版项目备份，段落结构与新版不兼容。请重新上传原始 PDF。"
    );
  }

  const project: LocalProject = {
    ...parsed.project,
    pdf: await dataUrlToBlob(parsed.project.pdf),
    updatedAt: Date.now()
  };
  await saveProject(project);
  return project;
}

export function consumeLayoutMigrationNotice(): boolean {
  try {
    const migrated = localStorage.getItem(MIGRATION_NOTICE_KEY) === "1";
    if (migrated) localStorage.removeItem(MIGRATION_NOTICE_KEY);
    return migrated;
  } catch {
    return false;
  }
}

export async function estimateStorage(): Promise<{
  usage: number;
  quota: number;
}> {
  const estimate = await navigator.storage?.estimate?.();
  return {
    usage: estimate?.usage ?? 0,
    quota: estimate?.quota ?? 0
  };
}
